// LaborReportTable.js
import React, { useState } from "react";
import { Table, Collapse, Button, Badge } from "reactstrap";
import PropTypes from "prop-types";
import formatDate from "constants/formatDate";
import formatCurrency from "constants/formatCurrency";

const LaborReportTable = ({ locations }) => {
    const [expandedLocations, setExpandedLocations] = useState({});
    const [expandedEmployees, setExpandedEmployees] = useState({});

    const toggleLocationExpand = (id) => {
        setExpandedLocations((prev) => ({ ...prev, [id]: !prev[id] }));
    };

    const toggleEmployeeExpand = (id) => {
        setExpandedEmployees((prev) => ({ ...prev, [id]: !prev[id] }));
    };

    const calcTotals = (shifts = [], locationId = null) => {
        let totalHours = 0;
        let totalCost = 0;
        let type = "-";

        const filteredShifts = locationId
            ? shifts.filter((s) => s.location_id === locationId)
            : shifts;

        filteredShifts.forEach((s) => {
            totalHours += s.net_hours || 0;
            if (s.pay_rate) {
                totalCost += (s.net_hours || 0) * s.pay_rate;
            }
            if (s.payment_type) {
                type = s.payment_type;
            }
        });

        const payRate = totalHours > 0 ? totalCost / totalHours : 0;

        return { totalHours, payRate, cost: totalCost, type };
    };

    const calcLocationTotals = (employees = [], locationId) => {
        let totalHours = 0;
        let totalCost = 0;
        employees.forEach((emp) => {
            const { totalHours: h, cost } = calcTotals(emp.shifts, locationId);
            totalHours += h;
            totalCost += cost;
        });
        return { totalHours, totalCost };
    };

    return (
        <div className="overflow-x-auto">
            <Table bordered responsive className="align-middle table-nowrap">
                <thead className="table-light">
                    <tr>
                        <th>Location</th>
                        <th>Hours</th>
                        <th>1099 Base Wage</th>
                        <th>W2 Base Wage</th>
                        <th>Total Base Wage</th>
                        <th>Tips</th>
                        {/* <th>Mgmt Fee</th> */}
                        <th></th>
                    </tr>
                </thead>
                <tbody>
                    {locations.slice()
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .map((loc) => {
                            const { totalHours, totalCost } = calcLocationTotals(loc.employees, loc.id);
                            if (totalHours == 0) {
                                return;
                            }
                            return (
                                <React.Fragment key={loc.id}>
                                    <tr>
                                        <td>{loc.name}</td>
                                        <td>{formatCurrency(totalHours)}</td>
                                        {(() => {
                                            // calculate 1099 & W2 base wages separately
                                            let total1099 = 0;
                                            let totalW2 = 0;

                                            loc.employees.forEach((emp) => {
                                                const { cost, type } = calcTotals(emp.shifts, loc.id);
                                                if (type === "W2") totalW2 += cost;
                                                else total1099 += cost;
                                            });

                                            const totalBase = total1099 + totalW2;

                                            return (
                                                <>
                                                    <td>${formatCurrency(total1099)}</td>
                                                    <td>${formatCurrency(totalW2)}</td>
                                                    <td>${formatCurrency(totalBase)}</td>
                                                </>
                                            );
                                        })()}
                                        <td>${formatCurrency(loc.total_tip || 0)}</td>
                                        {/* <td>${formatCurrency(loc.management_fee || 0)}</td> */}
                                        <td>
                                            {loc.employees.length > 0 && (
                                                <Button
                                                    className="button-gradient-bg"
                                                    onClick={() => toggleLocationExpand(loc.id)}
                                                >
                                                    {expandedLocations[loc.id] ? "Hide Employees" : "View Employees"}
                                                </Button>
                                            )}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td colSpan={4} className="p-0 border-0">
                                            <Collapse isOpen={expandedLocations[loc.id]}>
                                                <Table bordered responsive className="mb-0 table-striped">
                                                    <thead>
                                                        <tr>
                                                            <th>Employee</th>
                                                            <th>Pay Rate</th>
                                                            <th>Total Hours</th>
                                                            <th>Cost</th>
                                                            <th>Type</th>
                                                            <th></th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {[...loc.employees]
                                                            .sort((a, b) => {
                                                                const totalA = a.shifts.reduce((sum, s) => sum + (s.net_hours || 0), 0);
                                                                const totalB = b.shifts.reduce((sum, s) => sum + (s.net_hours || 0), 0);
                                                                return totalB - totalA; // high → low
                                                            })
                                                            .map((emp) => {
                                                                const { totalHours, payRate, cost, type } = calcTotals(emp.shifts, loc.id);
                                                                return (
                                                                    <React.Fragment key={emp.id}>
                                                                        <tr>
                                                                            <td>{emp.employee_alias || emp.full_name}</td>
                                                                            <td>${payRate.toFixed(2)}</td>
                                                                            <td>{totalHours.toFixed(2)}</td>
                                                                            <td>${formatCurrency(cost)}</td>
                                                                            <td>{type != "-" ? type : "1099"}</td>
                                                                            <td>
                                                                                {emp.shifts.filter(s => s.location_id === loc.id).length > 0 && (
                                                                                    <Button
                                                                                        className="button-gradient-bg"
                                                                                        onClick={() => toggleEmployeeExpand(`${emp.id}-${loc.id}`)}
                                                                                    >
                                                                                        {expandedEmployees[`${emp.id}-${loc.id}`] ? "Hide Shifts" : "View Shifts"}
                                                                                    </Button>
                                                                                )}
                                                                            </td>
                                                                        </tr>
                                                                        <tr>
                                                                            <td colSpan={6} className="p-0 border-0">
                                                                                <Collapse isOpen={expandedEmployees[`${emp.id}-${loc.id}`]}>
                                                                                    <Table bordered size="sm" responsive className="mb-0 table-striped">
                                                                                        <thead>
                                                                                            <tr>
                                                                                                <th>Start</th>
                                                                                                <th>End</th>
                                                                                                <th>Net Hours</th>
                                                                                                <th>Geofence Clock In / Out</th>
                                                                                            </tr>
                                                                                        </thead>
                                                                                        <tbody>
                                                                                            {emp.shifts
                                                                                                .filter((s) => s.location_id === loc.id)
                                                                                                .map((s) => (
                                                                                                    <tr key={s.id}>
                                                                                                        <td>{formatDate(s.actual_start_date)}</td>
                                                                                                        <td>{formatDate(s.actual_end_date)}</td>
                                                                                                        <td>{s.net_hours}</td>
                                                                                                        <td>
                                                                                                            <Badge
                                                                                                                className={s.inside_geofence_clockin == 1
                                                                                                                    ? 'bg-success'
                                                                                                                    : (s.inside_geofence_clockin == 0
                                                                                                                        ? 'bg-danger'
                                                                                                                        : 'bg-info')}>
                                                                                                                {s.inside_geofence_clockin == 1
                                                                                                                    ? `Yes ${s.clockin_distance} mile`
                                                                                                                    : (s.inside_geofence_clockin == 0
                                                                                                                        ? `No ${s.clockin_distance} mile`
                                                                                                                        : "NA")}
                                                                                                            </Badge>
                                                                                                            {" / "}
                                                                                                            <Badge
                                                                                                                className={s.inside_geofence_clockout == 1
                                                                                                                    ? 'bg-success'
                                                                                                                    : (s.inside_geofence_clockout == 0
                                                                                                                        ? 'bg-danger'
                                                                                                                        : 'bg-info')}>
                                                                                                                {s.inside_geofence_clockout == 1
                                                                                                                    ? `Yes ${s.clockout_distance} mile`
                                                                                                                    : (s.inside_geofence_clockout == 0
                                                                                                                        ? `No ${s.clockout_distance} mile`
                                                                                                                        : "NA")}
                                                                                                            </Badge>
                                                                                                        </td>
                                                                                                    </tr>
                                                                                                ))}
                                                                                        </tbody>
                                                                                    </Table>
                                                                                </Collapse>
                                                                            </td>
                                                                        </tr>
                                                                    </React.Fragment>
                                                                );
                                                            })}
                                                    </tbody>
                                                </Table>
                                            </Collapse>
                                        </td>
                                    </tr>
                                </React.Fragment>
                            );
                        })}
                    {locations.length === 0 && (
                        <tr>
                            <td colSpan={4} className="text-center py-3">
                                No locations found
                            </td>
                        </tr>
                    )}
                </tbody>
            </Table>
        </div>
    );
};

LaborReportTable.propTypes = {
    locations: PropTypes.array.isRequired, // [{ id, name, employees: [...] }]
};

export default LaborReportTable;